import { createClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey)

const missingSupabaseClient = new Proxy({} as SupabaseClient, {
  get() {
    throw new Error('Missing Supabase environment variables')
  },
})

if (!isSupabaseConfigured) {
  console.error('Missing Supabase environment variables')
}

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl as string, supabaseAnonKey as string)
  : missingSupabaseClient

export type UserRole = 'admin' | 'client'

export interface Profile {
  id: string
  email: string
  full_name?: string | null
  role: UserRole
  created_at: string
  updated_at: string
}

export interface ContactSubmission {
  id: string
  name: string
  email: string
  service: string
  message: string
  created_at: string
  read: boolean
  replied: boolean
  replied_by?: string | null
  replied_at?: string | null
}

export interface ProductOrder {
  id: string
  user_id: string
  tier: 'launch' | 'growth' | 'command'
  billing_mode: 'monthly' | 'buyout'
  status: 'draft' | 'pending_payment' | 'paid' | 'active' | 'in_progress' | 'completed' | 'cancelled'
  provider?: string | null
  provider_reference?: string | null
  total_amount?: number | null
  undiscounted_total_amount?: number | null
  discount_amount?: number | null
  currency: string
  addons?: string[] | null
  desired_domain?: string | null
  domain_needs_registration?: boolean | null
  domain_purchased?: boolean | null
  domain_purchased_at?: string | null
  domain_notes?: string | null
  pricing_snapshot?: Record<string, any> | null
  metadata?: Record<string, any> | null
  created_at: string
  updated_at: string
}

export interface ProjectIntake {
  id: string
  order_id: string
  user_id: string
  status: 'not_started' | 'collecting' | 'ready_for_review' | 'approved'
  summary?: string | null
  spec_checklist?: string[] | null
  last_ai_summary?: string | null
  desired_domain?: string | null
  domain_needs_registration?: boolean | null
  domain_purchased?: boolean | null
  domain_purchased_at?: string | null
  domain_notes?: string | null
  created_at: string
  updated_at: string
}

export interface ProjectIntakeMessage {
  id: string
  intake_id: string
  user_id: string
  role: 'client' | 'assistant' | 'system'
  content: string
  created_at: string
}

export interface ProjectMilestone {
  id: string
  order_id: string
  user_id: string
  title: string
  description?: string | null
  status: 'todo' | 'active' | 'done'
  position: number
  created_at: string
  updated_at: string
}

export interface CustomerRequest {
  id: string
  order_id: string
  user_id: string
  message: string
  status: 'new' | 'seen' | 'resolved'
  created_at: string
  updated_at: string
}

export interface ProductCatalogItem {
  id: string
  label: string
  description: string
  reason: string
  category: 'standard' | 'addon' | 'integration' | 'commerce' | 'ai' | 'seo'
  included_tiers: Array<'launch' | 'growth' | 'command'>
  suggested_tiers: Array<'launch' | 'growth' | 'command'>
  monthly_price: number
  once_off_price: number
  backend_group?: string | null
  uses_shared_backend?: boolean | null
  complementary_discount_rate?: number | null
  active: boolean
  customer_visible: boolean
  position: number
  created_at: string
  updated_at: string
}

export interface CustomerDomain {
  id: string
  order_id?: string | null
  user_id?: string | null
  sld: string
  tld: string
  full_domain: string
  source: 'domains_co_za' | string
  status:
    | 'requested'
    | 'availability_checked'
    | 'awaiting_payment'
    | 'paid'
    | 'registration_ready'
    | 'registration_in_progress'
    | 'registered'
    | 'dns_pending'
    | 'dns_configured'
    | 'vercel_connected'
    | 'gsc_pending'
    | 'gsc_verified'
    | 'live'
    | 'failed'
    | 'cancelled'
    | string
  availability_status: 'unchecked' | 'manual' | 'available' | 'unavailable' | 'premium' | 'invalid' | 'error' | string
  availability_checked_at?: string | null
  availability_response?: Record<string, any> | null
  is_available?: boolean | null
  is_premium?: boolean | null
  premium_reason?: string | null
  purchase_required: boolean
  purchase_paid: boolean
  purchase_paid_at?: string | null
  purchase_amount?: number | null
  purchase_currency: string
  registration_attempted_at?: string | null
  registration_completed_at?: string | null
  registrar_reference?: string | null
  registrar_response?: Record<string, any> | null
  registrant_name?: string | null
  registrant_email?: string | null
  registrant_phone?: string | null
  registrant_company?: string | null
  registrant_id_number?: string | null
  dns_mode: string
  nameservers?: string[] | null
  dns_records?: Array<Record<string, any>> | null
  dns_configured: boolean
  dns_configured_at?: string | null
  vercel_project_id?: string | null
  vercel_domain_added: boolean
  vercel_domain_verified: boolean
  vercel_response?: Record<string, any> | null
  gsc_property?: string | null
  gsc_verification_method?: string | null
  gsc_verification_record?: string | null
  gsc_verified: boolean
  gsc_verified_at?: string | null
  sitemap_submitted: boolean
  sitemap_submitted_at?: string | null
  renewal_due_at?: string | null
  auto_renew: boolean
  renewal_notes?: string | null
  admin_notes?: string | null
  created_at: string
  updated_at: string
}
